import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import BackupRestoreModal from "../components/BackupRestoreModal";
import {
  getAvailableBackups,
  restoreBudgetPortalBackup,
} from "../api/budgetPortal";
import { APP_NAME } from "../constants/app";
import { AppLoader } from "../components/Loaders";
import { BUDGET_MODULE_PATHS } from "../constants/budgetModule";
import { useAuth } from "../state/AuthContext";
import Logo from "../assets/image/logo.svg";

const RestoreBackupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");
  const cameFromSetup = useMemo(
    () => Boolean(location.state?.fromSetup),
    [location.state],
  );

  const getContinuePath = () =>
    cameFromSetup ? BUDGET_MODULE_PATHS.import : "/budget-entry";

  useEffect(() => {
    const loadBackups = async () => {
      setLoading(true);
      setError("");

      try {
        const backupsResponse = await getAvailableBackups(token);
        const availableBackups = backupsResponse?.data?.available_backups ?? [];
        setBackups(availableBackups);

        if (cameFromSetup && availableBackups.length === 0) {
          navigate(BUDGET_MODULE_PATHS.import, { replace: true });
          return;
        }
      } catch (backupError) {
        setError(
          backupError?.response?.data?.message ||
          backupError.message ||
          "Unable to load desktop backups.",
        );
      }

      setLoading(false);
    };

    loadBackups();
  }, [cameFromSetup, navigate, token]);

  const handleRestoreBackup = async (backup) => {
    setRestoring(true);
    setError("");

    try {
      await restoreBudgetPortalBackup(token, backup.latestBackupPath);
      navigate(getContinuePath(), { replace: true });
    } catch (restoreFailure) {
      setError(
        restoreFailure?.response?.data?.message ||
        restoreFailure.message ||
        "Failed to restore backup.",
      );
    }

    setRestoring(false);
  };

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return (
    <section className="splash-page">
      <div className="desktop-nav">
        <div className="desktop-navBrand">
          <img src={Logo} alt={APP_NAME} className="desktop-navLogo" />
          <p>{APP_NAME}</p>
        </div>
      </div>

      <div className="splash-bg">
        {loading ? (
          <AppLoader label="Loading desktop backups..." variant="page" />
        ) : (
          <p>Restore desktop backup</p>
        )}
        {error ? <div className="web-inlineError">{error}</div> : null}
        {!loading && backups.length === 0 && !error ? (
          <div className="web-inlineInfo">
            No desktop backup is available for this organisation.
          </div>
        ) : null}
        {!loading ? (
          <button
            type="button"
            className="landingPage-discardBtn"
            onClick={() => navigate(getContinuePath(), { replace: true })}
          >
            {cameFromSetup ? "Continue to Import" : "Continue to Budget"}
          </button>
        ) : null}
      </div>

      {!loading && backups.length > 0 ? (
        <BackupRestoreModal
          backups={backups}
          loading={restoring}
          error={error}
          onSelect={handleRestoreBackup}
          onCancel={() => navigate(getContinuePath(), { replace: true })}
        />
      ) : null}
    </section>
  );
};

export default RestoreBackupPage;
